/**
 * The admin shell. DESKTOP-FIRST (docs/06 §9): the target is ≥ 1280 px, it stays functional at
 * 1024, and below 768 it simply says "лучше открывать на компьютере" instead of pretending a
 * markup calculator and a nine-column orders board work on a phone. That is a deliberate MVP
 * scope decision, not an omission — Merdan works at a desk.
 */
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Monitor } from 'lucide-react';
import { PageTransition } from '@most/ui';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { CommandPalette, useCommandPalette } from './CommandPalette';
import { Bubbles, FizzLayer, SpotlightLayer } from '@/components/bold/Bold';
import { useAdminLiveUpdates } from '@/lib/live';
import { AdminCallProvider } from '@/features/inbox/AdminCallContext';

function DesktopOnlyNotice() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center md:hidden">
      <div className="flex h-14 w-14 items-center justify-center rounded bg-muted">
        <Monitor className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      </div>
      <h1 className="text-base font-semibold">{t('common.desktopOnly')}</h1>
      <p className="max-w-sm text-sm text-muted-foreground">{t('common.desktopOnlyHint')}</p>
    </div>
  );
}

export function AdminShell() {
  // One subscription for the whole app: the `role:admin` websocket topic invalidates the
  // dashboard, pricing queue, orders board and notification board as events land.
  useAdminLiveUpdates();

  const palette = useCommandPalette();

  // Keys the route-change crossfade. The Outlet renders whichever page matched the pathname, so
  // the pathname is *almost* the transition boundary — with one exception. Settings is a single
  // screen with in-page tabs (/settings/monitoring, /roles, /audit) whose tab bar carries a
  // framer-motion `layoutId` pill. If every sub-tab were its own transition key, `AnimatePresence
  // mode="wait"` would tear down and rebuild the whole SettingsPage shell on each tab click —
  // stranding that shared-layout pill mid-flight, so the tab appears not to switch. So all of
  // /settings collapses to ONE key: moving between its tabs is an in-page change (the pill slides,
  // the Outlet swaps), and only entering or leaving /settings crossfades.
  const location = useLocation();
  const routeKey = location.pathname.startsWith('/settings')
    ? '/settings'
    : location.pathname;

  return (
    // The call provider sits at shell level so an incoming support call rings on every
    // screen, not only while the inbox is open.
    <AdminCallProvider>
      <DesktopOnlyNotice />

      {/*
       * `h-screen` + an independently scrolling <main>, rather than a page that scrolls as a
       * whole. That is what lets the top bar and the table headers stick: with a single page
       * scroll they would need position:fixed and a manual offset, and the sticky <th> inside a
       * scrolling document behaves differently from one inside a scroll container.
       *
       * The outer shell is the RAIL's near-black, and the workspace is a rounded panel inset into
       * it. That inset is the whole trick behind the reference's depth: the dark ground shows
       * along the right and bottom edges, so the work surface reads as a card lying ON something
       * rather than as the page itself. Nothing but `p-2` and a radius produces it.
       */}
      <div className="hidden h-screen overflow-hidden bg-rail md:flex">
        <Sidebar />

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden py-2 pr-2">
          <div className="bold-scope relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-app shadow-overlay">
            {/* Ambient Bold dressing, BEHIND the workspace: a flavour-coloured bloom in the top
                corner and a thin stream of bubbles up the right edge. Kept faint and out of the
                table columns on purpose — this is still a screen for reading numbers. */}
            <div aria-hidden="true" className="bold-on-surface pointer-events-none absolute inset-0">
              <div className="bold-drift absolute -right-40 -top-56 h-[34rem] w-[34rem] rounded-full bg-primary/20 blur-3xl transition-colors duration-500" />
              <div className="bold-drift absolute -bottom-64 -left-40 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl transition-colors duration-500 [animation-delay:-11s] [animation-direction:alternate-reverse]" />
              <Bubbles count={9} rise={520} seed={23} className="left-auto w-40 opacity-50" />
            </div>

            <TopBar onOpenPalette={() => palette.setOpen(true)} />

            {/* The reading line: fills as <main> scrolls (a CSS scroll timeline, see index.css). */}
            <div aria-hidden="true" className="bold-progress pointer-events-none absolute inset-x-0 top-16 z-20 h-0.5 bg-primary" />

            {/* `scroll-main`: this is the app's page scrollbar — the shell is overflow-hidden, so
                the window never scrolls and <main> is the only global scroll surface there is. */}
            <main className="scroll-main relative min-w-0 flex-1 overflow-y-auto">
              {/* min-w keeps dense tables from collapsing at 1024; they scroll instead.
                  `h-full` propagates <main>'s definite height down so a page can opt into
                  filling the viewport (e.g. the inbox's own inner-scrolling two-pane) instead
                  of content-sizing — table pages still overflow and scroll <main> as before. */}
              <PageTransition
                routeKey={routeKey}
                className="mx-auto flex h-full min-h-full min-w-[60rem] max-w-[110rem] flex-col px-6 pb-8 pt-2"
              >
                <Outlet />
              </PageTransition>
            </main>
          </div>
        </div>
      </div>

      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />
      <FizzLayer />
      <SpotlightLayer />
    </AdminCallProvider>
  );
}
